/**
 * Homepage hero — full-bleed shop photography under a navy gradient.
 *
 * The marble-texture card and crest panel are gone: the landscape photo
 * already shows the real marble wall, the hex ceiling, the pool table and
 * the chairs in one frame, and a floating marble accent on top of a photo
 * of marble would be saying it twice. Mobile swaps to the portrait pool
 * shot because a 2:1 landscape crops to nothing at 390px wide.
 *
 * ─────────────────────────────────────────────────────────────
 * ONE SIGNAL NOW: POSTED HOURS
 * ─────────────────────────────────────────────────────────────
 * Desktop gets the appointment-desk card (bottom right); mobile gets the
 * compact status card below the subcopy. Both report the same thing — is
 * the building open — from the same shopHours source. The old staffing
 * pill (who is clocked in) is gone from the hero entirely, and with it the
 * Firestore reads it needed; staffing truth lives on /kiosk and /tv where
 * it is operational, not marketing.
 */

import { useCopy, useLocale } from '../../content';
import { useShopConfig } from '../../hooks/useShopConfig';
import { shortTime } from '../../lib/formatTime';
import {
  getFriendlyDay,
  isShopOpenNow,
  nextOpenDay,
  todayWindow,
  weekdayNameAt,
} from '../../lib/shopHours';
import { photoManifest } from '@/lib/photoManifest';

/** React 18 has no fetchPriority prop; the lowercase DOM attribute passes through. */
const EAGER = { fetchpriority: 'high' } as Record<string, string>;

export default function Hero() {
  const copy = useCopy();
  const c = copy.home.hero;
  const { t, shop, hours } = useShopConfig();
  const locale = useLocale();


  const tz = shop.timezone;
  const openNow = isShopOpenNow(hours, tz);
  const friendlyDay = getFriendlyDay(locale, hours, tz, {
    today: t(c.desk.today),
    tomorrow: t(c.desk.tomorrow),
  });

  /**
   * unstaffed covers the whole of an open day until closing — including the
   * hour before the doors open, which is exactly what "chairs open when the
   * doors do · today at 9am" describes. closed is after-close and off days,
   * and looks forward from tomorrow for its opening time.
   */
  const today = todayWindow(hours, tz);
  const dayStillAhead = Boolean(today?.stillAhead);
  // Day resolution lives in shopHours — the same source both status cards
  // use, so they cannot disagree about which day comes next.
  const nextOpening = (): { openTime: string; when: string } => {
    if (dayStillAhead && today) {
      return { openTime: shortTime(today.open, locale), when: '' };
    }
    const next = nextOpenDay(hours, tz);
    if (!next) return { openTime: '', when: '' };
    const when =
      next.ahead === 1
        ? t(c.liveStatus.whenTomorrowBare)
        : t(c.liveStatus.whenDayBare, { day: weekdayNameAt(locale, tz, next.ahead) });
    return { openTime: shortTime(next.open, locale), when };
  };


  return (
    <section className="relative min-h-[780px] overflow-hidden bg-navy lg:min-h-[860px]">
      <picture className="absolute inset-0">
        {/* Portrait pool shot under 768px — the landscape crops to a sliver there. */}
        <source
          media="(max-width: 767px)"
          srcSet={photoManifest.poolCinematic.hero.src}
          type="image/webp"
        />
        {/* From-entrance angle: hex ceiling upper-left, chairs receding down
            the right. The landscape shot made the marble wall the subject. */}
        <source
          srcSet={`${photoManifest.chairsRow.hero.src} 2400w, ${photoManifest.chairsRow.med.src} 1400w`}
          sizes="100vw"
          type="image/webp"
        />
        <img
          src={photoManifest.chairsRow.fallback.src}
          alt={photoManifest.chairsRow.alt}
          className="h-full w-full object-cover object-center"
          width={photoManifest.chairsRow.fallback.width}
          height={photoManifest.chairsRow.fallback.height}
          loading="eager"
          {...EAGER}
        />
      </picture>

      {/* Heavy left, open right — the copy sits left, the room shows right. */}
      <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/80 to-navy/10 lg:via-navy/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-navy/20" />

      <div className="relative mx-auto flex min-h-[780px] max-w-[1440px] flex-col justify-end px-6 pb-20 pt-32 md:px-10 lg:min-h-[860px] lg:px-14 lg:pb-24">
        <span className="mb-7 inline-flex w-fit items-center gap-3 rounded-pill border border-paper/25 bg-navy/40 px-5 py-2 backdrop-blur-sm">
          <img
            src="/assets/icons/pr-flag.png"
            alt=""
            width={30}
            height={19}
            className="block h-[19px] w-[30px] rounded-sm border border-paper/25 object-cover"
          />
          {/* Client correction (Sep 2026): the Boricua line is retired; the
              pill carries the flag + Est. year, and this is the page's only
              flag/EST render. boricuaPill stays in the dictionaries. */}
          <span className="font-newsreader text-[17px] text-paper/90">{t(c.est)}</span>
        </span>

        <h1 className="max-w-[720px] text-balance font-newsreader text-[56px] font-medium leading-[0.94] tracking-[-0.03em] text-paper sm:text-[84px] lg:text-[96px]">
          {t(c.headline)}
        </h1>

        {/* Client copy (Sep 2026), verbatim, two lines. It moved here from
            WelcomeBand, which is no longer rendered — the words belong next
            to the headline, not in a band under it. The old split subhead
            keys stay in the dictionaries, unrendered. */}
        <div className="mt-8 flex max-w-[460px] flex-col gap-3 font-newsreader text-[19px] leading-[1.55] text-paper/75">
          <p>{t(c.intro.line1)}</p>
          <p>{t(c.intro.line2)}</p>
        </div>

        {/* Mobile-only status card — same neon open/closed treatment as the
            desktop desk card, compacted. Status lines REUSE the desk and
            liveStatus keys: openToday when open, closedUntil (with its
            opening-time interpolation) when not, so the info the old pill
            carried survives the swap. */}
        <div className="mx-auto mt-6 flex w-full max-w-[340px] items-center gap-3 rounded-2xl bg-paper px-4 py-3 shadow-[0_12px_32px_-8px_rgba(11,27,58,0.4)] ring-1 ring-gold/30 lg:hidden">
          <span
            aria-hidden
            className={`h-3.5 w-3.5 shrink-0 rounded-full ${
              openNow ? 'animate-kd-pulse bg-gold' : 'bg-slate-body/40'
            }`}
          />
          <div className="flex-1 text-left">
            <p className="font-oswald text-[10px] font-medium uppercase tracking-[0.2em] text-navy/60">
              {t(c.mobileCard.label)}
            </p>
            <p className="font-newsreader text-[15px] leading-tight text-navy">
              {openNow ? t(c.openToday) : t(c.liveStatus.closedUntil, nextOpening())}
            </p>
          </div>
        </div>

        <div className="mt-9">
          {/* One CTA. The walk-in line is the in-shop tablet's job — a web
              signup path split the queue across two entry points. Gold, not
              navy: navy-on-navy vanished against the photo. */}
          <a
            href={shop.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block rounded-pill bg-gold px-8 py-4 font-newsreader text-[21px] text-navy transition-colors hover:bg-red-accent hover:text-white"
          >
            {t(c.ctaPrimary)}
          </a>
        </div>

      </div>

      {/* Task 4's appointment desk card — posted hours live here. bottom-6
          with no translate: the original translate-y-10 hung the card 40px
          past the hero's edge, straight into the TrustRibbon strip below. */}
      <div className="absolute bottom-6 right-[6%] hidden w-[340px] bg-paper p-6 shadow-[0_24px_60px_-12px_rgba(11,27,58,0.6)] ring-1 ring-gold/30 lg:block">
        <div className="mb-4 flex items-center justify-between border-b border-navy/15 pb-3">
          <p className="font-oswald text-[10px] font-medium uppercase tracking-[0.22em] text-navy">
            {t(c.appointmentDeskLabel)}
          </p>
          <span
            className={`flex items-center gap-2 font-oswald text-[10px] font-medium uppercase tracking-[0.14em] ${
              openNow ? 'text-slate-body' : 'text-slate-body/60'
            }`}
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${openNow ? 'bg-gold' : 'bg-slate-body/40'}`}
            />
            {openNow ? t(c.openToday) : t(c.closedToday)}
          </span>
        </div>

        <p className="font-newsreader text-[26px] font-medium leading-tight text-navy">
          {t(c.desk.line1)}
          <br />
          {t(c.desk.line2Prefix)} {friendlyDay}.
        </p>

        {/* No separate closed-state Booksy footnote any more: it existed
            because this button led to the in-house wizard, which could not
            take a booking for right now. Both are the same Booksy link
            today, so the footnote was two stacked links to one URL. Its
            desk.closedBooksyCta key stays in the dictionaries. */}
        <a
          href={shop.bookingUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 flex w-full items-center justify-between bg-navy px-5 py-3.5 font-oswald text-[11px] font-medium uppercase tracking-[0.16em] text-paper transition hover:bg-red-accent"
        >
          {t(c.desk.cta)}
          <span className="text-gold">→</span>
        </a>
      </div>
    </section>
  );
}
